import { create } from 'zustand';

import {
  listBedCategories,
  createBedCategory as apiCreateBedCategory,
  updateBedCategory as apiUpdateBedCategory,
  deactivateBedCategory as apiDeactivateBedCategory,
  setBedCategoryDepartments as apiSetBedCategoryDepartments,
} from '../services/api/bedCategoryApi';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Bed Category catalog store (B2 / S1–S3) — Zustand
 *
 * Clone of procedureStore.js MINUS the A4 picker/department slices (the
 * catalog is ORG-GLOBAL). Rows keyed by `bed_category_id`.
 *
 * Runtime safety (memory rule): the fetch is idempotent + single-flight —
 * a repeat call while loaded is a no-op, concurrent callers share ONE
 * in-flight promise — so a re-rendering consumer effect can never storm
 * requests. `includeInactive` is a backend filter → flipping it refetches
 * with force.
 * ═══════════════════════════════════════════════════════════════════════
 */

const useBedCategoryStore = create((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────
  bedCategories: [],
  isLoading: false,
  error: null,
  includeInactive: false,
  loaded: false,      // "already loaded" guard — repeat fetches no-op
  _inFlight: null,    // shared in-flight promise (single-flight)

  // ── Fetch (bounded: single-flight + loaded guard) ──────────────────
  fetchBedCategories: async ({ force = false } = {}) => {
    const { _inFlight, loaded } = get();
    if (_inFlight) return _inFlight; // concurrent callers share the promise
    if (loaded && !force) {
      return { success: true, data: { bed_categories: get().bedCategories } };
    }
    set({ isLoading: true, error: null });
    const promise = (async () => {
      const result = await listBedCategories({ include_inactive: get().includeInactive });
      if (result.success) {
        set({
          bedCategories: result.data.bed_categories || [],
          isLoading: false,
          loaded: true,
          _inFlight: null,
        });
      } else {
        set({ error: result.error, isLoading: false, _inFlight: null });
      }
      return result;
    })();
    set({ _inFlight: promise });
    return promise;
  },

  // ── Create → refetch (keeps server-derived fields + preset rows fresh) ──
  createBedCategory: async (body) => {
    const result = await apiCreateBedCategory(body);
    if (result.success) {
      await get().fetchBedCategories({ force: true });
    }
    return result;
  },

  // ── Update (partial) — patch the row in place by id ────────────────
  updateBedCategory: async (bed_category_id, body) => {
    const result = await apiUpdateBedCategory(bed_category_id, body);
    if (result.success) {
      set((s) => ({
        bedCategories: s.bedCategories.map((c) =>
          c.bed_category_id === bed_category_id ? { ...c, ...(result.data || body) } : c
        ),
      }));
    }
    return result;
  },

  // ── B5 / S2 — dept tagging ("Visible to"). Patches the row in place with
  // the server's post-flip owner_scope + department_ids (single source) so
  // the catalog + view-filter reflect the change immediately, no refetch. ──
  setBedCategoryDepartments: async (bed_category_id, department_ids) => {
    const result = await apiSetBedCategoryDepartments(bed_category_id, department_ids);
    if (result.success && result.data) {
      set((s) => ({
        bedCategories: s.bedCategories.map((c) =>
          c.bed_category_id === bed_category_id
            ? {
                ...c,
                owner_scope: result.data.owner_scope,
                department_ids: result.data.department_ids || [],
              }
            : c
        ),
      }));
    }
    return result;
  },

  // ── Deactivate (soft-delete) ───────────────────────────────────────
  deactivateBedCategory: async (bed_category_id) => {
    const result = await apiDeactivateBedCategory(bed_category_id);
    if (result.success) {
      const { includeInactive } = get();
      set((s) => ({
        bedCategories: includeInactive
          ? s.bedCategories.map((c) =>
              c.bed_category_id === bed_category_id ? { ...c, active: false } : c
            )
          : s.bedCategories.filter((c) => c.bed_category_id !== bed_category_id),
      }));
    }
    return result;
  },

  // ── Backend filter: refetch on flip ────────────────────────────────
  setIncludeInactive: (value) => {
    set({ includeInactive: !!value });
    return get().fetchBedCategories({ force: true });
  },

  clearError: () => set({ error: null }),
}));

export default useBedCategoryStore;
